import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from './api';

export interface Distributor {
  id: number;
  name: string;
  slug: string;
  website: string | null;
}

export interface PartSupplier {
  id: number;
  distributor_id: number;
  distributor_name: string;
  distributor_slug: string;
  distributor_website: string | null;
  distributor_pn: string;
  supplier_url: string | null;
  priority: number;
  price_unit_cents: number | null;
  currency: string;
  moq: number | null;
  order_multiple: number | null;
  lead_time_days: number | null;
  package_form: string | null;
  price_breaks: Array<Record<string, unknown>> | null;
  last_synced_at: string | null;
  sync_source: string;
  /** Set when bulk-refresh / validate finds this distributor PN
   * dead, or when the user manually flagged it. UI renders an "EOL"
   * pill on rows where this is true. */
  is_eol: boolean;
  eol_detected_at: string | null;
  /** `not_found_at_distributor` / `lifecycle_obsolete` /
   * `superseded_by_substitute` / `manual` — populates the tooltip. */
  eol_reason: string | null;
  /** Set when the user explicitly kept a dead supplier (tracking
   * residual stock). A future automatic re-flag is suppressed while
   * this is non-null. */
  eol_dismissed_at: string | null;
}

export interface SupplierCreateInput {
  distributor_id: number;
  distributor_pn: string;
  supplier_url?: string | null;
  priority?: number;
  price_unit_cents?: number | null;
  currency?: string;
  moq?: number | null;
  order_multiple?: number | null;
  lead_time_days?: number | null;
}

export type SupplierUpdateInput = Partial<SupplierCreateInput>;

export function useDistributors() {
  return useQuery<Distributor[]>({
    queryKey: ['distributors'],
    queryFn: () => api.get<Distributor[]>('/api/distributors'),
    staleTime: 5 * 60_000,
  });
}

export function useSuppliers(orgSlug: string | undefined, partId: number | null) {
  return useQuery<PartSupplier[]>({
    queryKey: ['suppliers', orgSlug, partId],
    queryFn: () =>
      api.get<PartSupplier[]>(`/api/orgs/${orgSlug}/parts/${partId}/suppliers`),
    enabled: Boolean(orgSlug && partId),
  });
}

export function useAddSupplier(orgSlug: string | undefined, partId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SupplierCreateInput) =>
      api.postJson<PartSupplier>(
        `/api/orgs/${orgSlug}/parts/${partId}/suppliers`,
        input,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['suppliers', orgSlug, partId] }),
  });
}

export function useUpdateSupplier(orgSlug: string | undefined, partId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SupplierUpdateInput & { id: number }) => {
      const { id, ...patch } = input;
      return api.patchJson<PartSupplier>(
        `/api/orgs/${orgSlug}/parts/${partId}/suppliers/${id}`,
        patch,
      );
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['suppliers', orgSlug, partId] }),
  });
}

export function useDeleteSupplier(orgSlug: string | undefined, partId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (supplierId: number) =>
      api.delete<unknown>(`/api/orgs/${orgSlug}/parts/${partId}/suppliers/${supplierId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['suppliers', orgSlug, partId] }),
  });
}

/**
 * Promote a supplier to primary (priority=1). Server reorders the rest
 * preserving relative order. Response is the full updated list, so the
 * cache write replaces in one go and we avoid a refetch flicker.
 */
export function usePromoteSupplier(orgSlug: string | undefined, partId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (supplierId: number) =>
      api.postJson<PartSupplier[]>(
        `/api/orgs/${orgSlug}/parts/${partId}/suppliers/${supplierId}/promote`,
        {},
      ),
    onSuccess: (rows) => {
      qc.setQueryData(['suppliers', orgSlug, partId], rows);
      // Parts list summary surfaces primary supplier — refresh it too.
      qc.invalidateQueries({ queryKey: ['parts', orgSlug] });
    },
  });
}


/**
 * Manually flag a supplier as dead (`eol_reason: "manual"`). Used
 * from the validate modal's NotFound branch and the drawer's
 * "Mark dead" affordance. Bulk-refresh sets the flag automatically
 * for adapter-detected NotFound; this is the user's explicit
 * counterpart for cases they noticed before the next refresh runs.
 */
export function useMarkSupplierEol(orgSlug: string | undefined, partId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (supplierId: number) =>
      api.postJson<PartSupplier>(
        `/api/orgs/${orgSlug}/parts/${partId}/suppliers/${supplierId}/mark-eol`,
        {},
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['suppliers', orgSlug, partId] }),
  });
}


/**
 * User explicitly keeps a dead supplier (tracking residual stock).
 * Clears `is_eol` but stamps `eol_dismissed_at` so a subsequent
 * automatic re-flag doesn't undo the user's intent.
 */
export function useDismissSupplierEol(
  orgSlug: string | undefined,
  partId: number,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (supplierId: number) =>
      api.postJson<PartSupplier>(
        `/api/orgs/${orgSlug}/parts/${partId}/suppliers/${supplierId}/dismiss-eol`,
        {},
      ),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['suppliers', orgSlug, partId] }),
  });
}
